import {
  getInitialSignupConfiguration,
  addInitialSignupConfiguration,
  deleteInitialSignupConfiguration,
  // Import SignupCreditsRequest for API payload type safety
  SignupCreditsRequest,
  GetInitialSignupConfigurationResponse,
  GetInitialSignupConfigurationParams,
  InitialSignupConfigAPI,
  InitialSignupUpdate,
  updateInitialSignupConfiguration,
} from "apis/adminServices/initialSignupServices";
import React, { useState, useEffect, memo, useCallback } from "react";
import SearchBar from "../searchbar";
import InitialSignupTable from "./InitialSignupTable";
import Pagination from "../pagination";
import { useToastStore } from "store/toastStore";
import AddInitialSignupModal, { InitialSignupFormConfig as AddModalFormConfig } from "./AddInitialSignupModal";
import ConfirmDeleteModal from "../DeleteModel";

export type { InitialSignupConfigAPI };

/**
 * ✅ FIX: Updated interface to ONLY include the three fields collected in the modal.
 */
type InitialSignupFormConfig = AddModalFormConfig;

const InitialSignupSetting: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [configurations, setConfigurations] = useState<InitialSignupConfigAPI[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ id: number; name: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const { showToast } = useToastStore();

  // ✅ FIX: State Initialization now correctly uses the simplified interface
  const [newConfig, setNewConfig] = useState<InitialSignupFormConfig>({
    configName: "",
    configurationType: "",
    creditAmount: 0,
    promoLimit: 0,
    flights: [],
    startDate: "",
    endDate: "",
    isActive: true,
  });

  const ROWS_PER_PAGE = 10;

  // ... (fetchInitialSignupConfigurations, useEffect, handleSearchChange, refreshTable, handleDelete remain the same) ...
  const fetchInitialSignupConfigurations = useCallback(async (search: string, page: number, pageSize: number) => {
    try {
      setLoading(true);

      const params: GetInitialSignupConfigurationParams = {
        Search: search.trim() || undefined,
        PageNumber: page,
        PageSize: pageSize,
      };

      const response: GetInitialSignupConfigurationResponse = await getInitialSignupConfiguration(params);

      setConfigurations(response.data);
      setTotalRecords(response.totalRecords);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch initial signup configurations");
      console.error("Fetch Error:", err);
      setConfigurations([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInitialSignupConfigurations(searchTerm, currentPage, ROWS_PER_PAGE);
  }, [searchTerm, currentPage, ROWS_PER_PAGE, fetchInitialSignupConfigurations]);

  const handleSearchChange = useCallback((newTerm: string) => {
    setSearchTerm(newTerm);
    setCurrentPage(1);
  }, []);

  const refreshTable = useCallback(() => {
    fetchInitialSignupConfigurations(searchTerm, currentPage, ROWS_PER_PAGE);
  }, [searchTerm, currentPage, fetchInitialSignupConfigurations, ROWS_PER_PAGE]);

  const handleDelete = (id: number, name: string) => {
    setItemToDelete({ id, name });
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;

    setIsDeleting(true);

    try {
      await deleteInitialSignupConfiguration(itemToDelete.id);
      refreshTable();
      showToast("Configuration deleted successfully", "success");
    } catch (error) {
      console.error("Deletion failed:", error);
      showToast("Failed to delete configuration", "error");
    } finally {
      setIsDeleteModalOpen(false);
      setItemToDelete(null);
      setIsDeleting(false);
    }
  };

  const cancelDelete = () => {
    if (isDeleting) return;
    setIsDeleteModalOpen(false);
    setItemToDelete(null);
  };

  /**
   * ✅ REFACTORED: Transformed handleAddConfig to include the new fields:
   * - configType (mapped to an assumed 'configType' in the API)
   * - flights (mapped to an assumed 'airlineIds' in the API)
   * * NOTE: You must ensure the actual SignupCreditsRequest interface and the API endpoint
   * are updated to accept `configType` and `airlineIds`.
   */
  const handleAddConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validation is now primarily handled in the modal, but keeping a server-side check here is good.
      if (newConfig.creditAmount <= 0) {
        throw new Error("Credit Amount must be greater than 0.");
      }
      if (newConfig.flights.length === 0) {
        throw new Error("Please select at least one flight.");
      }

      // API PAYLOAD CONSTRUCTION
      const apiConfig: SignupCreditsRequest = {
        CreditAmount: newConfig.creditAmount,
        PromoLimit: newConfig.promoLimit,
        ConfigName: newConfig.configName,
        ConfigurationType: newConfig.configurationType,
        AirlineIds: newConfig.flights,
        StartDate: newConfig.startDate,
        EndDate: newConfig.endDate,
        IsActive: newConfig.isActive,
      };

      const response = await addInitialSignupConfiguration(apiConfig);
      refreshTable();

      // ✅ Reset state including the new 'flights' field
      setNewConfig({
        configName: "",
        configurationType: "",
        creditAmount: 0,
        promoLimit: 0,
        flights: [],
        startDate: "",
        endDate: "",
        isActive: true,
      });
      setIsModalOpen(false);
      showToast(response.message || "Configuration added successfully", "success");
    } catch (error) {
      console.error("Error adding configuration:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to add configuration";
      showToast(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateConfig = useCallback(
    async (updatedConfig: InitialSignupUpdate) => {
      try {
        const response = await updateInitialSignupConfiguration(updatedConfig);
        showToast("Configuration updated successfully", "success");
        refreshTable();
      } catch (error) {
        console.error("Failed to update configuration:", error);
        showToast("Failed to update configuration", "error");
      }
    },
    [refreshTable, showToast]
  );

  return (
    <section className="bg-white p-6 rounded-xl mt-6">
      <div className="flex flex-wrap gap-4 items-center justify-between pb-4">
        <p className="text-grease-100 text-sm font-semibold">Promo Configuration</p>
        <div className="flex flex-wrap items-center gap-3">
          <SearchBar searchTerm={searchTerm} setSearchTerm={handleSearchChange} />
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            className="flex gap-2 p-3 border bg-[#FFD700] rounded-lg hover:bg-[#E6C200] transition-colors"
            disabled={loading}
          >
            <span className="st-add text-white" />
            <p className="font-medium text-white text-sm">Add New</p>
          </button>
        </div>
      </div>

      {error && (
        <div className="text-center py-4 text-red-600">
          {error}
          <button onClick={() => setError(null)} className="ml-2 text-blue-600">
            Dismiss
          </button>
        </div>
      )}

      <InitialSignupTable
        configurations={configurations}
        onDelete={handleDelete}
        loading={loading}
        onUpdate={handleUpdateConfig}
        refreshTable={refreshTable}
      />
      <Pagination
        currentPage={currentPage}
        totalItems={totalRecords}
        // Ensure this prop is passed for correct pagination display
        onPageChange={setCurrentPage}
      />
      {isModalOpen && (
        <AddInitialSignupModal
          newConfig={newConfig}
          setNewConfig={setNewConfig}
          onAdd={handleAddConfig}
          onClose={() => setIsModalOpen(false)}
          refreshTable={refreshTable}
        />
      )}

      <ConfirmDeleteModal
        isOpen={isDeleteModalOpen}
        configurationName={itemToDelete?.name || ""}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </section>
  );
};

export default memo(InitialSignupSetting);
